import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_PRODUCT_OPTION_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type ExcludeKeys, type ProductOptionFormModel } from "./type";
import { optionInputZodSchema, type OptionInputZodSchemaType } from "./validation-schema";

import type { ProductOptionFormModelContextType } from "../ui/_shared/context/context";

type Props = {
	inputModel: ProductOptionFormModel["InputModel"];
	queryData: ProductOptionFormModel["QueryData"];
};

export const useInitProductOptionFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Omit<ProductOptionFormModelContextType, "queryData"> & { resetViewModel: () => void } => {
	const hotelID = _inputModel.hotelID;
	const optionID = _inputModel.optionID;

	const defaultInputModel = DEFAULT_PRODUCT_OPTION_INPUT_MODEL({ optionID, hotelID, queryData });
	const inputModel = _inputModel ?? defaultInputModel;

	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			ProductOptionFormModel["InputModel"],
			ExcludeKeys,
			ProductOptionFormModel["QueryData"],
			OptionInputZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: inputModel,
			currentInput: inputModel,
			excludeKeys: excludeKeys,
			zodSchema: optionInputZodSchema,
		}),
	);

	const onSetViewModel: ProductOptionFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as ProductOptionFormModel["ViewModel"],
				),
			} satisfies ProductOptionFormModel["ViewModel"];

			const currentInput = viewModelToInputModelConverter<
				ProductOptionFormModel["InputModel"],
				ExcludeKeys,
				ProductOptionFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				ProductOptionFormModel["InputModel"],
				ExcludeKeys,
				ProductOptionFormModel["QueryData"],
				OptionInputZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: inputModel,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: optionInputZodSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, inputModel, viewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	return {
		viewModel,
		onSetViewModel,

		resetViewModel,
	};
};
